import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import CrossIcon from '@atlaskit/icon/core/cross';

import Image from '@atlaskit/image';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { Text } from '@atlaskit/primitives/compiled/text';

import { cssMap, cx } from '@atlaskit/css';

import { token } from '@atlaskit/tokens';

import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { type BannerVariant } from '../index';

import { messages, variantMessages } from '../messages';

import {
	type ConnectorProps,
	type OAuthSourceType,
	OAuthSource,
	ConnectorSources,
} from '@post-office/browser--rovo-expansion/search-page-results-inline';

import BannerImageLandscapeBackfill from '../../../../assets/banner-activation-images/desktop-and-tablet-landscape/backfill.png';

import BannerImagePortraitBackfill from '../../../../assets/banner-activation-images/tablet-portrait-and-below/backfill.png';

import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	bannerImage: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		objectPosition: 'left top',
	},
	imageContainer: {
		width: '260px',
		flexShrink: 0,
		alignSelf: 'stretch',
		overflow: 'hidden',
		position: 'relative',
	},
	imageContainerNarrow: {
		width: '180px',
	},
	contentContainer: {
		paddingBlockStart: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		minHeight: '140px',
	},
	contentContainerTall: {
		minHeight: '180px',
	},
	dismissButtonContainer: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 1,
	},
	container: {
		position: 'relative',
		borderRadius: token('radius.large'),
	},
});

export type SearchPageInline3PBannerContext = {
	productSource: string;
	variant: BannerVariant;
	onAuthorize: (oAuthSource: OAuthSourceType) => void;
	connector: ConnectorProps;
	teamUsagePercentage?: number;
};

const getVariantMessages = (mode: 'smartlinks' | 'full-connector', variant: BannerVariant) => {
	const modeMessages = variantMessages[mode];
	return modeMessages[variant as keyof typeof modeMessages];
};

const useBannerImages = (connectorId: string) => {
	const [bannerImageLandscape, setBannerImageLandscape] = useState(BannerImageLandscapeBackfill);
	const [bannerImagePortrait, setBannerImagePortrait] = useState(BannerImagePortraitBackfill);

	useEffect(() => {
		import(
			`../../../../assets/banner-activation-images/desktop-and-tablet-landscape/${connectorId}.png`
		)
			.then((image) => {
				setBannerImageLandscape(
					(image as { default: string })?.default || BannerImageLandscapeBackfill,
				);
			})
			.catch(() => {
				setBannerImageLandscape(BannerImageLandscapeBackfill);
			});

		import(
			`../../../../assets/banner-activation-images/tablet-portrait-and-below/${connectorId}.png`
		)
			.then((image) => {
				setBannerImagePortrait(
					(image as { default: string })?.default || BannerImagePortraitBackfill,
				);
			})
			.catch(() => {
				setBannerImagePortrait(BannerImagePortraitBackfill);
			});
	}, [connectorId]);

	return { bannerImageLandscape, bannerImagePortrait };
};

const useResponsiveBreakpoints = () => {
	const [isTallBanner, setIsTallBanner] = useState(false);
	const [isNarrowViewport, setIsNarrowViewport] = useState(false);
	const [shouldHideImage, setShouldHideImage] = useState(false);

	useEffect(() => {
		if (typeof document === 'undefined') {
			return undefined;
		}

		const heightQuery = globalThis.matchMedia('(max-width: 667px)');
		const narrowQuery = globalThis.matchMedia('(max-width: 558px)');
		const hideImageQuery = globalThis.matchMedia('(max-width: 470px)');

		const updateHeight = () => setIsTallBanner(heightQuery.matches);
		const updateNarrow = () => setIsNarrowViewport(narrowQuery.matches);
		const updateHideImage = () => setShouldHideImage(hideImageQuery.matches);

		updateHeight();
		updateNarrow();
		updateHideImage();

		if (heightQuery.addEventListener) {
			heightQuery.addEventListener('change', updateHeight);
			narrowQuery.addEventListener('change', updateNarrow);
			hideImageQuery.addEventListener('change', updateHideImage);
			return () => {
				heightQuery.removeEventListener('change', updateHeight);
				narrowQuery.removeEventListener('change', updateNarrow);
				hideImageQuery.removeEventListener('change', updateHideImage);
			};
		}

		heightQuery.addListener(updateHeight);
		narrowQuery.addListener(updateNarrow);
		hideImageQuery.addListener(updateHideImage);
		return () => {
			heightQuery.removeListener(updateHeight);
			narrowQuery.removeListener(updateNarrow);
			hideImageQuery.removeListener(updateHideImage);
		};
	}, []);

	return { isTallBanner, isNarrowViewport, shouldHideImage };
};

export const SearchPageInline3PBanner = ({
	context,
	onDismiss,
	onConnectLater,
}: {
	context: SearchPageInline3PBannerContext;
	onDismiss: (args?: { reason?: string }) => void;
	onConnectLater: () => void;
}): JSX.Element => {
	const { connector, variant, teamUsagePercentage, onAuthorize } = context;
	const { connectorName, connectorMode, connectorId } = connector;
	const { formatMessage } = useIntl();
	const { onCtaActioned } = usePostOfficeUserInteractions();

	const { bannerImageLandscape, bannerImagePortrait } = useBannerImages(connectorId);
	const { isTallBanner, isNarrowViewport, shouldHideImage } = useResponsiveBreakpoints();

	const handleDismiss = (reason: string) => onDismiss({ reason });

	const connectorNameToUse =
		connector.connectorProductKey === 'sharepoint' ? 'SharePoint' : connectorName;

	const connectorModeToUse =
		connectorMode === ConnectorSources.Smartlinks
			? ConnectorSources.Smartlinks
			: ConnectorSources.Full;
	const variantMsgs = getVariantMessages(connectorModeToUse, variant);
	const headingText = formatMessage(variantMsgs.heading, {
		connectorName: connectorNameToUse,
		teamUsagePercentage,
	});
	const bodyText = formatMessage(variantMsgs.body, {
		connectorName: connectorNameToUse,
		teamUsagePercentage,
	});
	const connectText = formatMessage(messages.connect, { connectorName: connectorNameToUse });
	const connectLaterText = formatMessage(messages.connectLater);
	const dismissLabel = formatMessage(messages.dismiss);
	const bannerImageAlt = formatMessage(messages.bannerImageAlt, {
		connectorName: connectorNameToUse,
	});

	return (
		<Box
			aria-labelledby={`rovo-search-page-inline-3p-${connectorModeToUse}-${variant}-banner`}
			backgroundColor="color.background.accent.blue.subtlest"
			xcss={styles.container}
			testId="rovo-search-page-inline-3p-connector-pre-auth-nudge"
		>
			<Inline spread="space-between" alignBlock="start">
				<Box xcss={cx(styles.contentContainer, isTallBanner && styles.contentContainerTall)}>
					<Stack space="space.100">
						<Heading
							id={`rovo-search-page-inline-3p-${connectorModeToUse}-${variant}-heading`}
							size="small"
						>
							{headingText}
						</Heading>
						<Text size="medium">{bodyText}</Text>
						<Box paddingBlockStart="space.100">
							<Inline space="space.100">
								<Button
									appearance="primary"
									onClick={() => {
										void onCtaActioned({ interactionIdentifier: 'connect-3p-banner' });
										onAuthorize(OAuthSource.searchPageOAuthBanner);
									}}
									testId="rovo-search-page-inline-3p-connect"
								>
									{connectText}
								</Button>
								<Button onClick={onConnectLater} testId="rovo-search-page-inline-3p-connect-later">
									{connectLaterText}
								</Button>
							</Inline>
						</Box>
					</Stack>
				</Box>
				{!shouldHideImage && (
					<Box xcss={cx(styles.imageContainer, isNarrowViewport && styles.imageContainerNarrow)}>
						<Image
							src={isNarrowViewport ? bannerImagePortrait : bannerImageLandscape}
							alt={bannerImageAlt}
							css={styles.bannerImage}
						/>
					</Box>
				)}
				<Box xcss={styles.dismissButtonContainer}>
					<IconButton
						appearance="subtle"
						icon={CrossIcon}
						label={dismissLabel}
						onClick={() => handleDismiss('dismiss-button')}
						testId="rovo-search-page-inline-3p-dismiss"
					/>
				</Box>
			</Inline>
		</Box>
	);
};
